import {
  MAX_NOTE_CONTENT_LENGTH,
  MAX_NOTE_TYPE_LENGTH,
  MIN_NOTE_CONTENT_LENGTH,
  decide,
  type EncounterResponse,
} from '@emr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage } from '../../../api/client.ts';
import { listEncounters } from '../../../api/encounters.ts';
import { createNote, listNotes } from '../../../api/notes.ts';
import { useGrants } from '../../../auth/sessionContext.ts';
import { LoadingNotice } from '../../../components/AsyncState.tsx';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import {
  Badge,
  Button,
  ErrorText,
  Field,
  Panel,
  SelectField,
  TextArea,
} from '../../../components/primitives.tsx';
import { formatDateTime } from '../../../lib/dateFormat.ts';
import { encounterStatusTone } from '../encounterStatus.ts';
import { QueryFailureNotice } from '../QueryFailureNotice.tsx';
import { ChartTable } from './ChartTable.tsx';
import { NoteCard } from './NoteCard.tsx';

/**
 * Encounters (read-only) and notes (view, draft, sign, co-sign) for one patient — `docs/roadmap.md`
 * Phase 3 C5/C6, `BACKLOG.md` C10a. Combined in one tab deliberately: the note composer's encounter
 * picker reads from the same list the encounters section displays, and the two are one continuous
 * story — "what happened, and what was documented about it" — not two unrelated screens.
 *
 * Starting a new encounter (`POST .../encounters`, `chart:write`) is out of scope here: the composer
 * picks from a patient's *existing* encounters (or none, for a freestanding note); it does not
 * create one. `api/encounters.ts`'s own header names this the same way.
 *
 * The encounters query is fetched twice in this file's own source — once here (feeding the
 * composer's `<select>`, which needs the raw list) and once inside `ChartTable` below (rendering
 * the table) — but not twice over the wire: both use the identical `['patient', patientId,
 * 'encounters']` query key, so TanStack Query serves the second `useQuery` call from the same
 * cache entry the first one already populated.
 */

function encounterColumnsFor(
  t: TFunction<'patientChartTabs'>,
): readonly DataTableColumn<EncounterResponse>[] {
  return [
    {
      key: 'type',
      header: t('encountersNotes.encounterColumns.type'),
      cell: (encounter) => encounter.encounterType,
      // `encounterType` is free text up to 100 chars (`shared/src/encounterRequests.ts`), the same
      // unbounded shape every other free-text column in this diff caps — see `DataTable.tsx`'s own
      // header comment on why the wrap class alone does not constrain a table column by itself.
      wrap: 'wrap',
      maxWidthCh: 22,
    },
    {
      key: 'status',
      header: t('encountersNotes.encounterColumns.status'),
      cell: (encounter) => (
        <Badge tone={encounterStatusTone(encounter.status)}>{encounter.status}</Badge>
      ),
      wrap: 'nowrap',
    },
    {
      key: 'started',
      header: t('encountersNotes.encounterColumns.started'),
      cell: (encounter) => formatDateTime(encounter.startedAt),
      wrap: 'nowrap',
    },
    {
      key: 'ended',
      header: t('encountersNotes.encounterColumns.ended'),
      cell: (encounter) => (encounter.endedAt === null ? '—' : formatDateTime(encounter.endedAt)),
      wrap: 'nowrap',
    },
  ];
}

function noteTypeSuggestionsFor(t: TFunction<'patientChartTabs'>): readonly string[] {
  return [
    t('encountersNotes.composer.noteTypeSuggestions.progressNote'),
    t('encountersNotes.composer.noteTypeSuggestions.historyPhysical'),
    t('encountersNotes.composer.noteTypeSuggestions.consultNote'),
    t('encountersNotes.composer.noteTypeSuggestions.dischargeSummary'),
    t('encountersNotes.composer.noteTypeSuggestions.telephoneEncounter'),
    t('encountersNotes.composer.noteTypeSuggestions.nurseNote'),
  ];
}

export function EncountersNotesTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  const grants = useGrants();
  const encounters = useQuery({
    queryKey: ['patient', patientId, 'encounters'],
    queryFn: ({ signal }) => listEncounters(patientId, signal),
  });
  const notes = useQuery({
    queryKey: ['patient', patientId, 'notes'],
    queryFn: ({ signal }) => listNotes(patientId, signal),
  });

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h3 className="mt-0 mb-2 text-base font-semibold text-ink">
          {t('encountersNotes.encountersHeading')}
        </h3>
        <ChartTable
          queryKey={['patient', patientId, 'encounters']}
          queryFn={(signal) => listEncounters(patientId, signal)}
          columns={encounterColumnsFor(t)}
          rowKey={(encounter) => encounter.id}
          minWidthRem={44}
          emptyMessage={t('encountersNotes.encountersEmpty')}
          loadingLabel={t('encountersNotes.encountersLoading')}
          caption={t('encountersNotes.encountersCaption')}
        />
      </section>

      {/* `chart:write`, hidden rather than disabled-with-reason — matching `NoteCard.tsx`'s own
          sign/cosign gating in this same tab, and `shell/navigation.ts`'s "capability missing →
          hidden" convention: a role with `chart:read` but not `chart:write` (`referring_provider`)
          has no business documenting a chart at all, so showing a form it can only fail to submit
          is noise, not a discoverable feature. Server-side enforcement (`requireCapability` on
          `POST .../notes`) was already correct; this only fixes the client staying silent about it
          until a 403 lands after the form is filled in. */}
      {decide(grants, 'chart:write') ? (
        <section>
          <h3 className="mt-0 mb-2 text-base font-semibold text-ink">
            {t('encountersNotes.startNoteHeading')}
          </h3>
          <Panel>
            <NoteComposer
              patientId={patientId}
              encounters={encounters.isSuccess ? encounters.data : []}
            />
          </Panel>
        </section>
      ) : null}

      <section>
        <h3 className="mt-0 mb-2 text-base font-semibold text-ink">
          {t('encountersNotes.notesHeading')}
        </h3>
        {notes.isPending ? (
          <LoadingNotice label={t('encountersNotes.notesLoading')} />
        ) : notes.isError ? (
          <QueryFailureNotice error={notes.error} onRetry={() => void notes.refetch()} />
        ) : notes.data.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">{t('encountersNotes.notesEmpty')}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {notes.data.map((note) => (
              <NoteCard key={note.id} note={note} patientId={patientId} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function NoteComposer({
  patientId,
  encounters,
}: {
  readonly patientId: string;
  readonly encounters: readonly EncounterResponse[];
}) {
  const { t } = useTranslation('patientChartTabs');
  const queryClient = useQueryClient();
  const noteTypeListId = useId();

  const [encounterId, setEncounterId] = useState('');
  const [noteType, setNoteType] = useState('');
  const [content, setContent] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createNote(patientId, {
        noteType: noteType.trim(),
        content: content.trim(),
        encounterId: encounterId === '' ? undefined : encounterId,
      }),
    onSuccess: () => {
      setEncounterId('');
      setNoteType('');
      setContent('');
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId, 'notes'] });
    },
  });

  const canSubmit = noteType.trim() !== '' && content.trim() !== '';

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) create.mutate();
      }}
      className="flex flex-col gap-3"
    >
      <SelectField
        label={t('encountersNotes.composer.encounterLabel')}
        value={encounterId}
        onChange={(event) => {
          setEncounterId(event.target.value);
        }}
        hint={t('encountersNotes.composer.encounterHint')}
      >
        <option value="">{t('encountersNotes.composer.noEncounter')}</option>
        {encounters.map((encounter) => (
          <option key={encounter.id} value={encounter.id}>
            {encounter.encounterType} — {formatDateTime(encounter.startedAt)} ({encounter.status})
          </option>
        ))}
      </SelectField>

      <Field
        label={t('encountersNotes.composer.noteTypeLabel')}
        list={noteTypeListId}
        value={noteType}
        onChange={(event) => {
          setNoteType(event.target.value);
        }}
        required
        maxLength={MAX_NOTE_TYPE_LENGTH}
        placeholder={t('encountersNotes.composer.noteTypePlaceholder')}
      />
      <datalist id={noteTypeListId}>
        {noteTypeSuggestionsFor(t).map((suggestion) => (
          <option key={suggestion} value={suggestion} />
        ))}
      </datalist>

      <TextArea
        label={t('encountersNotes.composer.contentLabel')}
        value={content}
        onChange={(event) => {
          setContent(event.target.value);
        }}
        required
        minLength={MIN_NOTE_CONTENT_LENGTH}
        maxLength={MAX_NOTE_CONTENT_LENGTH}
        rows={6}
        placeholder={t('encountersNotes.composer.contentPlaceholder')}
      />

      {create.isError ? <ErrorText message={errorMessage(create.error)} /> : null}

      <Button type="submit" pending={create.isPending} disabled={!canSubmit} className="self-start">
        {create.isPending
          ? t('encountersNotes.composer.submitting')
          : t('encountersNotes.composer.submit')}
      </Button>
    </form>
  );
}
